import { IdAgendamento, IdConta, IdContato, IdGrupo, IdInbox, IdTemplate, IdUsuario } from "../common-types"
import { ResponseContato } from "../contatos/contatos"
import { ResponseGrupo } from "../grupos/grupos.service"
import { GenericServiceWithReturns } from "../generic-service"
import { ResponseInbox } from "../inbox/inbox.service"
import { ResponseTemplate } from "../templates/templates.service"
import { ResponseUsuario } from "../usuarios/usuarios.service"
import { IMetaParams as MetaTemplateParams } from "../templates-wpp-meta/meta-wpp-template.service"

export enum TipoAgendamento {
    Texto        = 'TEXTO',
    Anexo        = 'ANEXO',
    Template     = 'TEMPLATE',
    MetaTemplate = 'META_TEMPLATE',
    Bot          = 'BOT',
}

export enum StatusAgendamento {
    Enviado     = 'ENVIADO',
    Aguardando  = 'AGUARDANDO',
    Falha       = 'FALHA',
    Processando = 'PROCESSANDO',
}

export class AgendamentoDTO {
    constructor(base: AgendamentoDTO = AgendamentoDTO.getDefault()) { 
        this.id            = base.id
        this.tipo          = base.tipo
        this.datetimeEnvio = base.datetimeEnvio
        this.mensagem      = base.mensagem
        this.legenda       = base.legenda
        this.url           = base.url
        this.idContato     = base.idContato
        this.idGrupo       = base.idGrupo
        this.idUsuario     = base.idUsuario
        this.idInbox       = base.idInbox
        this.idTemplate    = base.idTemplate
        this.idMetaTemplate     = base.idMetaTemplate
        this.metaTemplateParams = base.metaTemplateParams
        this.idBot         = base.idBot
        this.status        = base.status
        this.enviarComAssinatura = base.enviarComAssinatura
        // Read-only: o servidor preenche no envio. Copiado para o histórico não sumir
        // ao passar um agendamento por este construtor.
        this.mensagemEnviada = base.mensagemEnviada
    }
    public static getDefault(): AgendamentoDTO {
        return {
            id            : null,
            tipo          : TipoAgendamento.Texto,
            datetimeEnvio : new Date(),
            mensagem      : null,
            legenda       : null,
            url           : null,
            idContato     : undefined,
            idGrupo       : undefined,
            idUsuario     : undefined,
            idInbox       : undefined,
            status        : StatusAgendamento.Aguardando,
            enviarComAssinatura: false,
        }
    }

    id           ?: IdAgendamento|null
    tipo          : TipoAgendamento
    status        : StatusAgendamento
    datetimeEnvio : string|Date
    
    /** Template: guarda as chaves de variável cruas. Resolvido no envio pelo servidor. */
    mensagem?: string|null
    legenda ?: string|null
    url     ?: string|null

    /**
     * Texto que REALMENTE foi enviado — variáveis já resolvidas e assinatura já
     * prefixada. Preenchido pelo servidor só no instante do envio: é null enquanto
     * pendente e nos tipos sem texto livre (META_TEMPLATE, BOT). Somente leitura.
     */
    mensagemEnviada?: string|null

    idContato ?: IdContato
    idGrupo   ?: IdGrupo
    idUsuario ?: IdUsuario
    idInbox   ?: IdInbox
    idTemplate?: IdTemplate
    idMetaTemplate?: string|null
    metaTemplateParams?: MetaTemplateParams|null
    idBot?: string|null

    contato ?: ResponseContato
    grupo   ?: ResponseGrupo
    usuario ?: ResponseUsuario
    inbox   ?: ResponseInbox
    template?: ResponseTemplate
    bot     ?: { id: string; nome: string }|null

    emLote?: boolean
    enviarComAssinatura?: boolean
}

export type UpdateAgendamento = AgendamentoDTO & {id: IdAgendamento}

export interface IAgendamentoService {
    getAll()                                  : Promise<AgendamentoDTO[]>
    getAllByInbox(idInbox: IdInbox)           : Promise<AgendamentoDTO[]>
    getAllByUser(idUser: IdUsuario)           : Promise<AgendamentoDTO[]>
    create(newAgendamento: AgendamentoDTO)    : Promise<AgendamentoDTO>
    update(updatedAgendamento: AgendamentoDTO): Promise<AgendamentoDTO>
    delete(idAgendamento: string)             : Promise<AgendamentoDTO>
}

export class AgendamentoService extends GenericServiceWithReturns<AgendamentoDTO, UpdateAgendamento, AgendamentoDTO> implements IAgendamentoService {
    constructor() { super('agendamentos') }
    async getAllByConta(idConta: IdConta): Promise<AgendamentoDTO[]> {
        return await this.f.get(`/conta/${idConta}`) as AgendamentoDTO[];
    }
    async getAllByInbox(idInbox: IdInbox): Promise<AgendamentoDTO[]> {
        return await this.f.get(`/inbox/${idInbox}`) as AgendamentoDTO[];
    }
    async getAllByUser(idUser: IdUsuario): Promise<AgendamentoDTO[]> {
        return await this.f.get(`/usuario/${idUser}`) as AgendamentoDTO[];
    }
}